import { DatePipe } from "@angular/common";
import { Injectable } from "@angular/core";
import { Month } from "@core/enums/month.enum";
import { BaseService } from "@core/services/base.service";
import { LoadingService } from "@core/services/loading.service";
import { RelativeDateRange } from "@features/search/enums/relative-date-range.enum";
import { TranslateService } from "@ngx-translate/core";
import * as Sentry from "@sentry/browser";

@Injectable({
  providedIn: "root"
})
export class DateService extends BaseService {
  // List of officially supported locales for date formatting
  private readonly SUPPORTED_LOCALES = [
    "en",
    "en-GB",
    "de",
    "es",
    "fr",
    "it",
    "pt",
    "zh-hans",
    "el",
    "fi",
    "ja",
    "hu",
    "nl",
    "pl",
    "uk",
    "ru",
    "sq",
    "tr"
  ];

  constructor(
    public readonly loadingService: LoadingService,
    public readonly translateService: TranslateService,
    public readonly datePipe: DatePipe
  ) {
    super(loadingService);
  }

  datePipeTransform(dateObj: Date, format: string, timezone?: string, locale?: string): string {
    try {
      // Add validation check
      if (isNaN(dateObj.getTime())) {
        throw new Error("Invalid Date object created");
      }

      // Determine which locale to use
      let effectiveLocale = locale;

      // If no locale was specifically provided, use the current language
      if (!effectiveLocale) {
        effectiveLocale = this.translateService.currentLang;
      }

      // Check if the effective locale is in our supported list, otherwise fallback to en-GB
      if (effectiveLocale && !this.SUPPORTED_LOCALES.includes(effectiveLocale.toLowerCase())) {
        effectiveLocale = "en-GB";
      }

      const result: string | null = this.datePipe.transform(dateObj, format, timezone, effectiveLocale);

      if (result === null) {
        throw new Error("DatePipe returned null");
      }

      return result;
    } catch (e) {
      const logData = {
        error: e,
        dateObj: dateObj,
        format: format,
        timezone: timezone,
        locale: locale,
        currentLang: this.translateService.currentLang,
        dateToString: dateObj.toString(),
        dateToISO: dateObj.toISOString(),
        dateGetTime: dateObj.getTime()
      };

      if (typeof Sentry !== "undefined") {
        Sentry.captureException(e, {
          extra: logData
        });
      }

      console.error("Date formatting error:", logData);

      // Even in error cases, try one more time with en-GB
      try {
        return (
          this.datePipe.transform(dateObj, format, timezone, "en-GB") || this.translateService.instant("Invalid date")
        );
      } catch {
        return this.translateService.instant("Invalid date");
      }
    }
  }

  todayISODate() {
    // Returns today's date in YYYY-MM-DD format.
    return new Date().toISOString().split("T")[0];
  }

  calculateRelativeDateRange(range: RelativeDateRange): { min: string; max: string } {
    const today = new Date();
    const todayUTC = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));

    let min: Date;
    let max: Date;

    switch (range) {
      case RelativeDateRange.TODAY:
        min = new Date(todayUTC);
        max = new Date(todayUTC);
        break;

      case RelativeDateRange.YESTERDAY:
        min = new Date(todayUTC);
        min.setUTCDate(todayUTC.getUTCDate() - 1);
        max = new Date(min);
        break;

      case RelativeDateRange.THIS_WEEK:
        // Get Monday of current week
        min = new Date(todayUTC);
        const dayOfWeek = todayUTC.getUTCDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        min.setUTCDate(todayUTC.getUTCDate() - daysToMonday);
        // Get Sunday of current week
        max = new Date(min);
        max.setUTCDate(min.getUTCDate() + 6);
        break;

      case RelativeDateRange.THIS_MONTH:
        min = new Date(Date.UTC(todayUTC.getUTCFullYear(), todayUTC.getUTCMonth(), 1));
        max = new Date(Date.UTC(todayUTC.getUTCFullYear(), todayUTC.getUTCMonth() + 1, 0));
        break;

      case RelativeDateRange.THIS_YEAR:
        min = new Date(Date.UTC(todayUTC.getUTCFullYear(), 0, 1));
        max = new Date(Date.UTC(todayUTC.getUTCFullYear(), 11, 31));
        break;

      case RelativeDateRange.LAST_7_DAYS:
        min = new Date(todayUTC);
        min.setUTCDate(todayUTC.getUTCDate() - 6);
        max = new Date(todayUTC);
        break;

      case RelativeDateRange.LAST_30_DAYS:
        min = new Date(todayUTC);
        min.setUTCDate(todayUTC.getUTCDate() - 29);
        max = new Date(todayUTC);
        break;

      case RelativeDateRange.LAST_365_DAYS:
        min = new Date(todayUTC);
        min.setUTCDate(todayUTC.getUTCDate() - 364);
        max = new Date(todayUTC);
        break;
    }

    return {
      min: this.toISODate(min),
      max: this.toISODate(max)
    };
  }

  humanizeRelativeDateRange(range: RelativeDateRange): string {
    const map = {
      [RelativeDateRange.TODAY]: this.translateService.instant("Today"),
      [RelativeDateRange.YESTERDAY]: this.translateService.instant("Yesterday"),
      [RelativeDateRange.THIS_WEEK]: this.translateService.instant("This week"),
      [RelativeDateRange.THIS_MONTH]: this.translateService.instant("This month"),
      [RelativeDateRange.THIS_YEAR]: this.translateService.instant("This year"),
      [RelativeDateRange.LAST_7_DAYS]: this.translateService.instant("Last 7 days"),
      [RelativeDateRange.LAST_30_DAYS]: this.translateService.instant("Last 30 days"),
      [RelativeDateRange.LAST_365_DAYS]: this.translateService.instant("Last 365 days")
    };

    return map[range];
  }

  private toISODate(date: Date): string {
    return date.toISOString().split("T")[0];
  }

  getCurrentYear(): number {
    return new Date().getFullYear();
  }

  humanizeMonth(month: Month): string {
    const map = {
      [Month.JAN]: this.translateService.instant("January"),
      [Month.FEB]: this.translateService.instant("February"),
      [Month.MAR]: this.translateService.instant("March"),
      [Month.APR]: this.translateService.instant("April"),
      [Month.MAY]: this.translateService.instant("May"),
      [Month.JUN]: this.translateService.instant("June"),
      [Month.JUL]: this.translateService.instant("July"),
      [Month.AUG]: this.translateService.instant("August"),
      [Month.SEP]: this.translateService.instant("September"),
      [Month.OCT]: this.translateService.instant("October"),
      [Month.NOV]: this.translateService.instant("November"),
      [Month.DEC]: this.translateService.instant("December")
    };

    return map[month];
  }

  formatDates(dates: string[]): string {
    if (dates.length === 0) {
      return "";
    }

    // Convert dates to timestamps, filter out nulls and invalid dates, remove duplicates, and remove different times on the same day
    const parsedDates = Array.from(
      new Set(
        dates
          .filter(dateStr => dateStr && !isNaN(Date.parse(dateStr)))
          .map(dateStr => new Date(new Date(dateStr).toISOString().split("T")[0]).getTime())
      )
    ).sort((a, b) => a - b);

    // If the array is empty after filtering, return an empty string
    if (parsedDates.length === 0) {
      return "";
    }

    if (parsedDates.length === 1) {
      return this._formatSingleDate(parsedDates[0]);
    }

    const contiguousRanges = this.getContiguousRanges(parsedDates);

    if (parsedDates.length === 2 && contiguousRanges.length === 2) {
      const date1 = new Date(parsedDates[0]);
      const date2 = new Date(parsedDates[1]);
      const currentYear = this.getCurrentYear();

      // If dates are in the same year but not current year, format differently
      if (date1.getFullYear() === date2.getFullYear() && date1.getFullYear() !== currentYear) {
        const dateFormat = this._getDateFormat();
        const currentLang = this.translateService.currentLang;
        const formattedDate1 = this.datePipeTransform(date1, dateFormat, "UTC", currentLang);
        const formattedDate2 = this.datePipeTransform(date2, dateFormat, "UTC", currentLang);
        return `${formattedDate1}, ${formattedDate2} ${date1.getFullYear()}`;
      }

      return `${this._formatSingleDate(parsedDates[0])}, ${this._formatSingleDate(parsedDates[1])}`;
    }

    if (contiguousRanges.length === 1) {
      const range = contiguousRanges[0];
      if (range.length === 1) {
        return this._formatSingleDate(range[0]);
      }
      return this._formatRange(range);
    }

    return this._formatNonContiguous(parsedDates);
  }

  public getContiguousRanges(dates: number[]): number[][] {
    const ranges: number[][] = [];
    let currentRange: number[] = [dates[0]];

    for (let i = 1; i < dates.length; i++) {
      const prevDate = dates[i - 1];
      const currentDate = dates[i];

      const oneDay = 24 * 60 * 60 * 1000;

      if (currentDate - prevDate <= oneDay) {
        currentRange.push(currentDate);
      } else {
        ranges.push(currentRange);
        currentRange = [currentDate];
      }
    }

    ranges.push(currentRange);

    return ranges;
  }

  /**
   * Gets the appropriate date format based on locale
   * @param locale Optional locale to use instead of current language
   * @returns Date format string (either "MMM d" for US or "d MMM" for others)
   */
  private _getDateFormat(locale?: string): string {
    const effectiveLocale = locale || this.translateService.currentLang;
    return effectiveLocale === "en-US" ? "MMM d" : "d MMM";
  }

  private _formatSingleDate(date: number, forceYear = false): string {
    const dateObj = new Date(date);

    const currentYear = this.getCurrentYear();
    const currentLang = this.translateService.currentLang;
    const dateFormat = this._getDateFormat(currentLang);

    if (dateObj.getFullYear() === currentYear && !forceYear) {
      return this.datePipeTransform(dateObj, dateFormat, "UTC", currentLang);
    }

    const format = `${dateFormat}${dateFormat === "MMM d" ? "," : ""} yyyy`;
    return this.datePipeTransform(dateObj, format, "UTC", currentLang);
  }

  private _formatRange(range: number[]): string {
    const startDate = new Date(range[0]);
    const endDate = new Date(range[range.length - 1]);
    const currentYear = this.getCurrentYear();
    const currentLang = this.translateService.currentLang;
    const dateFormat = this._getDateFormat(currentLang);

    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();

    if (startYear !== endYear) {
      // If the range spans different years, show the year for both dates
      return `${this._formatSingleDate(range[0])} - ${this._formatSingleDate(
        range[range.length - 1],
        startYear !== currentYear
      )}`;
    }

    if (startYear === currentYear) {
      // If the range is within the current year, omit the year
      if (startDate.getMonth() !== endDate.getMonth()) {
        const formattedStartDate = this.datePipeTransform(startDate, dateFormat, "UTC", currentLang);
        const formattedEndDate = this.datePipeTransform(endDate, dateFormat, "UTC", currentLang);
        return `${formattedStartDate} - ${formattedEndDate}`;
      }

      if (dateFormat === "MMM d") {
        const month = this.datePipeTransform(endDate, "MMM", "UTC", currentLang);
        const startDay = this.datePipeTransform(startDate, "d", "UTC", currentLang);
        const endDay = this.datePipeTransform(endDate, "d", "UTC", currentLang);
        return `${month} ${startDay}-${endDay}`;
      }

      const startDay = this.datePipeTransform(startDate, "d", "UTC", currentLang);
      const formattedEndDate = this.datePipeTransform(endDate, dateFormat, "UTC", currentLang);
      return `${startDay}-${formattedEndDate}`;
    }

    // If the range is within the same year (but not the current year), include the year once at the end
    if (startDate.getMonth() !== endDate.getMonth()) {
      if (dateFormat === "MMM d") {
        const formattedStartDate = this.datePipeTransform(startDate, "MMM d", "UTC", currentLang);
        const formattedEndDate = this.datePipeTransform(endDate, "MMM d, yyyy", "UTC", currentLang);
        return `${formattedStartDate} - ${formattedEndDate}`;
      }

      const formattedStartDate = this.datePipeTransform(startDate, dateFormat, "UTC", currentLang);
      const formattedEndDate = this.datePipeTransform(endDate, dateFormat + " yyyy", "UTC", currentLang);
      return `${formattedStartDate} - ${formattedEndDate}`;
    }

    if (dateFormat === "MMM d") {
      const formattedStartDate = this.datePipeTransform(startDate, "MMM d", "UTC", currentLang);
      const formattedEndDay = this.datePipeTransform(endDate, "d, yyyy", "UTC", currentLang);
      return `${formattedStartDate}-${formattedEndDay}`;
    }

    const startDay = this.datePipeTransform(startDate, "d", "UTC", currentLang);
    const formattedEndDate = this.datePipeTransform(endDate, dateFormat + " yyyy", "UTC", currentLang);
    return `${startDay}-${formattedEndDate}`;
  }

  private _formatNonContiguous(dates: number[]): string {
    const firstDate = new Date(dates[0]);
    const lastDate = new Date(dates[dates.length - 1]);
    const currentLang = this.translateService.currentLang;

    const allInSameYear = firstDate.getFullYear() === lastDate.getFullYear();
    const allInSameMonth = allInSameYear && firstDate.getMonth() === lastDate.getMonth();

    if (allInSameMonth) {
      const formattedMonth = this.datePipeTransform(firstDate, "MMM yyyy", "UTC", currentLang);
      return `${dates.length} days in ${formattedMonth}`;
    }

    if (allInSameYear) {
      const formattedYear = this.datePipeTransform(firstDate, "yyyy", "UTC", currentLang);
      return `${dates.length} days in ${formattedYear}`;
    }

    return `${dates.length} days`;
  }
}
